import { cn } from '../../../lib/utils';
import type { ExternalIntelCompositeRow } from '../../../api/intelClient';
import { BandChip } from './BandChip';
import { ProvenanceTooltip } from './ProvenanceTooltip';
import { UnavailablePlaceholder } from './UnavailablePlaceholder';

interface CompositeRowProps {
  row: ExternalIntelCompositeRow;
}

/**
 * One row in the External Intelligence panel — represents a single
 * `(composite_type, region)` pair from the WorldMonitor composite response.
 *
 * Row anatomy (post-bug-190 brief §Render rules 3):
 *   [region · composite_type label · band chip · freshness mark]
 *
 * `availability_reason !== 'available'` swaps the row content for an
 * `UnavailablePlaceholder` — sprint-1 scaffolds; sprint-2 (FR-2.3)
 * wires the full failure-UX.
 *
 * Hover over the row reveals the provenance tooltip (producer, baseline,
 * timestamps, freshness_tier, external_id, composed_of, tooltip_text).
 * No numeric `composite_value` ever leaves the backend, so no numeric
 * value appears here — FR-1.2.a / FR-1.2.d.
 */
export function CompositeRow({ row }: CompositeRowProps) {
  // Schema-blocked / stale / network-error / dead-letter — swap content
  // for the placeholder. The placeholder still carries the region label
  // so the operator can correlate failed regions across polls.
  if (row.availability_reason !== 'available') {
    return (
      <li
        data-testid={`external-intel-row-${row.region ?? 'unregioned'}`}
        data-region={row.region ?? ''}
        data-composite-type={row.composite_type}
        data-band={row.band}
        data-availability-reason={row.availability_reason}
      >
        <UnavailablePlaceholder
          reason={row.availability_reason}
          region={row.region}
        />
      </li>
    );
  }

  return (
    <li
      data-testid={`external-intel-row-${row.region ?? 'unregioned'}`}
      data-region={row.region ?? ''}
      data-composite-type={row.composite_type}
      data-band={row.band}
      data-availability-reason={row.availability_reason}
      className={cn(
        'group relative',
        'flex items-center gap-2 px-2 py-1.5',
        'border-t border-[var(--e-border-secondary)]/30 first:border-t-0',
        'text-[11px] font-mono',
        'transition-colors hover:bg-[var(--e-bg-hover)]/40',
      )}
    >
      <ProvenanceTooltip row={row}>
        <span
          className={cn(
            'inline-flex items-center gap-2 cursor-help',
            'min-w-0 flex-1',
          )}
        >
          {row.region && (
            <span className="uppercase tracking-wider text-[var(--e-text-secondary)] min-w-[2.5rem]">
              {row.region}
            </span>
          )}
          <span className="text-[var(--e-text-muted)] truncate">
            {humanizeCompositeType(row.composite_type)}
          </span>
        </span>
      </ProvenanceTooltip>

      <BandChip band={row.band} />

      <FreshnessMark tier={row.freshness_tier} />
    </li>
  );
}

/** Convert backend Pydantic Literal labels to operator-readable copy.
 * Today only `riskScore` ships (post-bug-190 D-2); the others are
 * aspirational and the backend SQL filter rejects them. When WorldMonitor
 * starts emitting them, extend this map in lockstep. */
function humanizeCompositeType(compositeType: string): string {
  switch (compositeType) {
    case 'riskScore':
      return 'Risk score';
    case 'crossSourceSignal':
      return 'Cross-source signal';
    case 'theaterPosture':
      return 'Theatre posture';
    default:
      return compositeType;
  }
}

interface FreshnessMarkProps {
  tier: ExternalIntelCompositeRow['freshness_tier'];
}

/** Compact freshness indicator — single glyph + tier label. Same
 * achromatic discipline as the band chip; tier conveyed by weight + dot
 * fill, not hue. */
function FreshnessMark({ tier }: FreshnessMarkProps) {
  const fill = FRESHNESS_FILL[tier];
  return (
    <span
      data-testid={`external-intel-freshness-${tier}`}
      title={`freshness: ${tier}`}
      className={cn(
        'inline-flex items-center gap-1 min-w-[3rem]',
        'text-[10px] font-mono uppercase tracking-wider',
        'text-[var(--e-text-muted)]',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block w-1.5 h-1.5 rounded-full border border-[var(--e-text-muted)]/50',
          fill,
        )}
      />
      {tier}
    </span>
  );
}

const FRESHNESS_FILL: Record<ExternalIntelCompositeRow['freshness_tier'], string> = {
  fresh: 'bg-[var(--e-text-primary)]',
  aging: 'bg-[var(--e-text-primary)]/50',
  stale: 'bg-[var(--e-text-primary)]/20',
  expired: 'bg-transparent',
};
